import { useState } from "react";
import { IonInput, IonItem, IonList } from "@ionic/react";
import { Button, Colors, Icon } from "swing-components";

import { GET, translate } from "~utils";

import { GeocodeData } from "../FilterBar/types";
import { isZipCodeValid } from "../LocationSearch/LocationSearch";

// Types

type ZipcodeActionSheetProps = {
  onDismiss: () => void;
  onApply: (zipcode: string, geocode: GeocodeData) => void;
  currentZipcode: string;
  recentZipcodes: string[];
  userZipcode: string;
  userState: string;
};

type GeocodeParams = { zipcode: string } | { latitude: number; longitude: number };

type GeocodeResponse = { data?: GeocodeData; error?: string };

// Styles

const styles = {
  content: {
    padding: "0 16px",
    flex: 1,
  },
  container: {
    position: "relative",
    overflow: "scroll",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    position: "sticky",
    top: 0,
    padding: "32px 16px 16px",
    background: "white",
    display: "flex",
    justifyContent: "space-between",
    zIndex: 999,
  },
  inputWrapper: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    border: `1px solid ${Colors.slate300}`,
    borderRadius: 8,
    padding: "8px 12px",
  },
  locationButton: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 0",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 14,
    width: "100%",
  },
  divider: {
    width: "100%",
    height: 1,
    backgroundColor: Colors.slate200,
  },
  error: {
    color: Colors.red500,
    fontSize: 12,
    margin: "0 0 8px 0",
  },
  footer: {
    position: "sticky",
    bottom: 0,
    padding: 16,
    background: "white",
    display: "flex",
    gap: 16,
  },
} as const;

// Helpers

async function fetchGeocode(params: GeocodeParams): Promise<GeocodeResponse> {
  const response = await GET("/api/sub/location/geocode", {
    queryParams: params,
  });
  return response as GeocodeResponse;
}

function RecentZipcodesList({
  zipcodes,
  userZipcode,
  onSelect,
}: {
  zipcodes: string[];
  userZipcode: string;
  onSelect: (zip: string) => void;
}) {
  if (zipcodes.length === 0) return null;

  return (
    <>
      <p css={{ fontWeight: 600, fontSize: 14, margin: "16px 0 8px 0" }}>
        {translate("LOCATION_FILTER_RECENT_LOCATIONS")}
      </p>
      <IonList css={{ "--ion-item-background": "transparent" }}>
        {zipcodes.map((zip) => {
          const isUserZip = zip === userZipcode;
          return (
            <IonItem
              key={zip}
              lines="none"
              button
              onClick={() => onSelect(zip)}
              css={{ "--padding-start": 0, cursor: "pointer" }}
            >
              <div css={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Icon name={isUserZip ? "Home" : "Clock"} color={Colors.slate400} />
                <span>
                  {zip}
                  {isUserZip && (
                    <span css={{ color: Colors.slate400, marginLeft: 8, fontSize: 12 }}>
                      ({translate("LOCATION_FILTER_YOUR_ZIPCODE")})
                    </span>
                  )}
                </span>
              </div>
            </IonItem>
          );
        })}
      </IonList>
    </>
  );
}

// Main Component

export function ZipcodeActionSheet({
  onDismiss,
  onApply,
  currentZipcode,
  recentZipcodes,
  userZipcode,
  userState,
}: ZipcodeActionSheetProps) {
  const [zipcode, setZipcode] = useState(currentZipcode);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValidZip = isZipCodeValid(zipcode);
  const hasChanged = zipcode !== currentZipcode;
  const canApply = isValidZip && hasChanged && !isLoading;

  const validateAndApply = async (geocode: GeocodeData, zip: string) => {
    if (userState && geocode.state !== userState) {
      setError(translate("LOCATION_FILTER_OUT_OF_STATE"));
      return false;
    }
    onApply(zip, geocode);
    onDismiss();
    return true;
  };

  const handleUseCurrentLocation = async () => {
    if (!navigator.geolocation) {
      setError(translate("LOCATION_FILTER_GEOLOCATION_NOT_SUPPORTED"));
      return;
    }

    setIsLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      async ({ coords: { latitude, longitude } }) => {
        try {
          const { data, error: apiError } = await fetchGeocode({ latitude, longitude });
          if (data?.zipcode && isZipCodeValid(data.zipcode)) {
            if (await validateAndApply(data, data.zipcode)) return;
            setZipcode(data.zipcode);
          } else {
            setError(apiError || translate("LOCATION_FILTER_COULD_NOT_DETERMINE_ZIP"));
          }
        } catch {
          setError(translate("LOCATION_FILTER_FAILED_TO_GET_LOCATION"));
        } finally {
          setIsLoading(false);
        }
      },
      () => {
        setError(translate("LOCATION_FILTER_UNABLE_TO_RETRIEVE"));
        setIsLoading(false);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
    );
  };

  const handleApply = async () => {
    if (!isValidZip) return;

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: apiError } = await fetchGeocode({ zipcode });
      if (data) {
        await validateAndApply(data, zipcode);
      } else {
        setError(apiError || translate("LOCATION_FILTER_COULD_NOT_DETERMINE_ZIP"));
      }
    } catch {
      setError(translate("LOCATION_FILTER_FAILED_TO_GET_LOCATION"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleZipcodeInput = (value: string) => {
    setError(null);
    setZipcode(value.replace(/[^0-9]/g, "").slice(0, 5));
  };

  const loadingText = translate("LOCATION_FILTER_GETTING_LOCATION");

  return (
    <div css={styles.container}>
      <div css={styles.header}>
        <span css={{ fontSize: 20 }}>{translate("LOCATION_FILTER_ENTER_ZIPCODE")}</span>
        <div css={{ cursor: "pointer" }} onClick={onDismiss}>
          <Icon name="Close" color={Colors.blue500} />
        </div>
      </div>

      <div css={styles.content}>
        <IonItem lines="none" css={{ "--padding-start": 0 }}>
          <div css={styles.inputWrapper}>
            <Icon name="Search" />
            <IonInput
              type="text"
              inputMode="numeric"
              maxlength={5}
              placeholder={translate("LOCATION_FILTER_ZIPCODE_PLACEHOLDER")}
              value={zipcode}
              onIonInput={(e) => handleZipcodeInput(e.detail.value || "")}
              css={{ "--padding-start": 0 }}
            />
          </div>
        </IonItem>

        <p css={{ fontWeight: 600, fontSize: 14, margin: "16px 0 8px 0" }}>
          {translate("LOCATION_FILTER_NEARBY_LOCATIONS")}
        </p>
        <button css={styles.locationButton} onClick={handleUseCurrentLocation} disabled={isLoading}>
          <Icon name="Location" color={Colors.blue500} />
          {isLoading ? loadingText : translate("LOCATION_FILTER_USE_CURRENT_LOCATION")}
        </button>

        {error && <p css={styles.error}>{error}</p>}

        <div css={styles.divider}></div>
        <RecentZipcodesList
          zipcodes={recentZipcodes}
          userZipcode={userZipcode}
          onSelect={setZipcode}
        />
      </div>

      <div css={styles.footer}>
        <div css={{ flex: 1 }}>
          <Button type="tertiary" onClick={onDismiss} isFluid>
            {translate("LABEL_CANCEL")}
          </Button>
        </div>
        <div css={{ flex: 1 }}>
          <Button
            status={canApply ? "enabled" : "disabled"}
            onClick={handleApply}
            type="primary"
            isFluid
          >
            {isLoading ? loadingText : translate("LOCATION_MODAL_APPLY_BUTTON")}
          </Button>
        </div>
      </div>
    </div>
  );
}
